import { useEffect, useRef, useState } from "react";
import type { AdminSessionResponse } from "../../shared/admin-auth";

interface GoogleIdentity {
  initialize(options: { client_id: string; nonce: string; auto_select: boolean; callback: (result: { credential: string }) => void }): void;
  renderButton(element: HTMLElement, options: { theme: string; size: string; text: string; width: number }): void;
}
declare global { interface Window { google?: { accounts: { id: GoogleIdentity } } } }

export function GoogleLogin({ onSignIn }: { onSignIn: (session: AdminSessionResponse) => void }) {
  const button = useRef<HTMLDivElement>(null);
  const [problem, setProblem] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const challengeRequest = useRef<Promise<{ clientId: string; nonce: string }> | null>(null);

  useEffect(() => {
    let cancelled = false;
    setProblem(""); setReady(false);
    async function load() {
      // Share the initial request across StrictMode's effect replay so two
      // responses cannot race to replace the browser's challenge cookie.
      challengeRequest.current ??= fetch("/api/auth/google/challenge", { method: "POST", credentials: "same-origin" }).then(async (response) => {
        const value = await response.json() as { clientId: string; nonce: string; error?: string };
        if (!response.ok) throw new Error(value.error ?? "Could not start Google sign-in.");
        return value;
      });
      const challenge = await challengeRequest.current;
      if (cancelled) return;
      if (!window.google?.accounts.id) await new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>("script[data-google-sign-in]");
        const script = existing ?? document.createElement("script");
        script.addEventListener("load", () => resolve(), { once: true });
        script.addEventListener("error", () => { script.remove(); reject(new Error("Google sign-in could not load. Please try again.")); }, { once: true });
        if (!existing) {
          script.src = "https://accounts.google.com/gsi/client";
          script.async = true; script.dataset.googleSignIn = "true";
          document.head.appendChild(script);
        }
      });
      if (cancelled || !button.current || !window.google) return;
      window.google.accounts.id.initialize({
        client_id: challenge.clientId, nonce: challenge.nonce, auto_select: false,
        callback: ({ credential }) => {
          if (cancelled) return;
          setBusy(true); setProblem("");
          void fetch("/api/auth/google", {
            method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ credential }),
          }).then(async (result) => {
            const session = await result.json() as AdminSessionResponse & { error?: string };
            if (cancelled) return;
            if (!result.ok || !session.user) throw new Error(session.error ?? "Sign-in was not accepted.");
            onSignIn(session);
          }).catch((error: unknown) => { if (!cancelled) { setProblem(error instanceof Error ? error.message : "Sign-in failed."); setReady(false); } })
            .finally(() => { if (!cancelled) setBusy(false); });
        },
      });
      button.current.replaceChildren();
      window.google.accounts.id.renderButton(button.current, { theme: "outline", size: "large", text: "signin_with", width: Math.max(200, Math.min(280, button.current.clientWidth)) });
      setReady(true);
    }
    void load().catch((error: unknown) => { if (!cancelled) setProblem(error instanceof Error ? error.message : "Could not load Google sign-in."); });
    return () => { cancelled = true; };
  }, [attempt, onSignIn]);

  return <main className="admin-login"><section className="login-card">
    <div className="brand-mark">W</div><p className="eyebrow">Secure admin</p><h1>Manage your shop assistant</h1>
    <p>Sign in with your authorized Google account. Access is restricted to approved administrators.</p>
    <div ref={button} className="google-sign-in" hidden={Boolean(problem) || busy} />
    {!problem && (!ready || busy) && <p role="status">{busy ? "Verifying your account…" : "Loading Google sign-in…"}</p>}
    {problem && <><p role="alert">{problem}</p><button className="button button-secondary" onClick={() => { challengeRequest.current = null; setAttempt((value) => value + 1); }}>Try again</button></>}
    <p className="login-security-note">Your Google password is never shared with this app.</p>
  </section></main>;
}
